<script setup lang="ts">
/**
 * A vertical rail of round icon buttons that switches between a handful of
 * views — the narrow column beside a record („Korrespondenz · Aktivitäten ·
 * Dokumente“) or a module's tab rail. The label is the tooltip and the
 * accessible name; a count badge sits on the button's corner.
 *
 * `v-model` is the active item's key. Two semantics:
 * - `tabs` (default): an ARIA tablist (vertical). One button is a tab stop,
 *   ↑/↓ (and Home/End) move AND select — the automatic-activation tab
 *   pattern. Point `controls` at the panel's id to wire `aria-controls`.
 * - `nav`: a `<nav>` of buttons with `aria-current`; every button is a tab
 *   stop, ↑/↓ only move focus.
 *
 * Badges: `0`, `''`, `null` and `undefined` show nothing (pass the string
 * `'0'` for a visible zero). The badge is part of the accessible name —
 * „Aktivitäten (3)“, or „Aktivitäten, 3 offen“ with `badgeLabel`. `tone`
 * colours it; `danger` for overdue, `warning` for waiting.
 *
 * The `end` slot takes extra buttons under the items (an ⓘ, a settings cog) —
 * an `NxIconButton`, with its own `badge`, for one that carries a count.
 */
import { computed, ref, type Component } from 'vue';
import { NxTooltip, type NxTooltipProps } from '../Tooltip';
import { countBadgeName, hasCountBadge } from '../lib/countBadge';
import NxCountBadge from '../lib/NxCountBadge.vue';

export interface NxIconRailItem {
  key: string | number;
  label: string;
  icon: Component;
  badge?: number | string | null;
  /** Spoken meaning of the badge („3 offen“). */
  badgeLabel?: string;
  badgeTone?: 'brand' | 'neutral' | 'warning' | 'danger';
  disabled?: boolean;
  /** Id of the panel this tab shows (`tabs` mode). */
  controls?: string;
}

const props = withDefaults(
  defineProps<{
    items: readonly NxIconRailItem[];
    mode?: 'tabs' | 'nav';
    /** Accessible name of the rail. */
    ariaLabel?: string;
    size?: 'sm' | 'md';
    tooltipSide?: NxTooltipProps['side'];
  }>(),
  { mode: 'tabs', ariaLabel: 'Ansichten', size: 'md', tooltipSide: 'right' }
);

const model = defineModel<string | number | null>({ default: null });
const emit = defineEmits<{ select: [key: string | number] }>();
defineSlots<{ end?: () => unknown }>();

const isTabs = computed(() => props.mode === 'tabs');

function accessibleName(item: NxIconRailItem): string {
  return countBadgeName(item.label, item.badge, item.badgeLabel);
}

const activeIndex = computed(() => props.items.findIndex((i) => i.key === model.value));

/** In `tabs` mode the one tab stop: the active item, else the first enabled one. */
const tabStop = computed(() => {
  const at = activeIndex.value;
  if (at >= 0 && !props.items[at]!.disabled) return at;
  return props.items.findIndex((i) => !i.disabled);
});

function select(item: NxIconRailItem) {
  if (item.disabled) return;
  if (model.value !== item.key) model.value = item.key;
  emit('select', item.key);
}

const root = ref<HTMLElement | null>(null);
/** Buttons by item index, read from the DOM — `ref` arrays in `v-for` keep no order. */
function buttonAt(index: number): HTMLButtonElement | undefined {
  return root.value?.querySelectorAll<HTMLButtonElement>('.nx-icon-rail__button')[index];
}

function onKeydown(event: KeyboardEvent, index: number) {
  if (event.altKey || event.ctrlKey || event.metaKey) return;
  const count = props.items.length;
  const enabled = (i: number) => !props.items[i]!.disabled;
  let target = -1;
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    const step = event.key === 'ArrowDown' ? 1 : -1;
    for (let i = 1; i <= count && target < 0; i++) {
      const at = (index + step * i + count * count) % count;
      if (enabled(at)) target = at;
    }
  } else if (event.key === 'Home') {
    target = props.items.findIndex((i) => !i.disabled);
  } else if (event.key === 'End') {
    for (let at = count - 1; at >= 0 && target < 0; at--) if (enabled(at)) target = at;
  }
  if (target < 0) return;
  event.preventDefault();
  buttonAt(target)?.focus();
  if (isTabs.value) select(props.items[target]!);
}
</script>

<template>
  <component
    :is="isTabs ? 'div' : 'nav'"
    ref="root"
    class="nx-icon-rail"
    :class="`size-${props.size}`"
    :aria-label="isTabs ? undefined : props.ariaLabel"
    data-testid="nx-icon-rail"
  >
    <component
      :is="isTabs ? 'div' : 'ul'"
      class="nx-icon-rail__list"
      :role="isTabs ? 'tablist' : undefined"
      :aria-orientation="isTabs ? 'vertical' : undefined"
      :aria-label="isTabs ? props.ariaLabel : undefined"
    >
      <component
        :is="isTabs ? 'div' : 'li'"
        v-for="(item, index) in props.items"
        :key="item.key"
        class="nx-icon-rail__item"
        :role="isTabs ? 'none' : undefined"
      >
        <NxTooltip :text="item.label" :side="props.tooltipSide">
          <button
            type="button"
            class="nx-icon-rail__button"
            :class="{ 'is-active': index === activeIndex }"
            :role="isTabs ? 'tab' : undefined"
            :aria-selected="isTabs ? index === activeIndex : undefined"
            :aria-controls="isTabs ? item.controls : undefined"
            :aria-current="!isTabs && index === activeIndex ? 'true' : undefined"
            :aria-label="accessibleName(item)"
            :tabindex="isTabs ? (index === tabStop ? 0 : -1) : undefined"
            :disabled="item.disabled"
            :data-key="item.key"
            data-testid="nx-icon-rail-button"
            @click="select(item)"
            @keydown="onKeydown($event, index)"
          >
            <component :is="item.icon" filled class="nx-icon-rail__icon" aria-hidden="true" />
            <NxCountBadge
              v-if="hasCountBadge(item.badge)"
              class="nx-icon-rail__badge"
              :badge="item.badge"
              :tone="item.badgeTone"
            />
          </button>
        </NxTooltip>
      </component>
    </component>
    <div v-if="$slots.end" class="nx-icon-rail__end">
      <slot name="end" />
    </div>
  </component>
</template>

<style scoped>
.nx-icon-rail {
  --icon-rail-button: var(--nxt-control-size-md);
  --icon-rail-icon: var(--nxt-icon-size-lg);

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--nxt-space-sm);
  padding: var(--nxt-space-sm);
}
.nx-icon-rail.size-sm {
  --icon-rail-button: var(--nxt-control-size-sm);
  --icon-rail-icon: var(--nxt-icon-size-md);

  padding: var(--nxt-space-xs);
}

.nx-icon-rail__list {
  display: flex;
  flex-direction: column;
  /* Room for the badge that sits on each button's upper corner. */
  gap: var(--nxt-space-sm);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: inherit;
}

.nx-icon-rail__item,
.nx-icon-rail__item :deep(.tooltip-wrapper) {
  display: flex;
}

.nx-icon-rail__end {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding-block-start: var(--nxt-space-xs);
  border-block-start: var(--nxt-hairline) solid var(--nxt-border);
}

.nx-icon-rail__button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--icon-rail-button);
  height: var(--icon-rail-button);
  padding: 0;
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-surface-bg);
  color: var(--nxt-fg-subtle);
  box-shadow: var(--nxt-shadow-sm);
  cursor: pointer;
  transition:
    background-color var(--nxt-motion-fast) var(--nxt-ease-out),
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    color var(--nxt-motion-fast) var(--nxt-ease-out),
    transform var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-icon-rail__button:hover:not(:disabled, .is-active) {
  border-color: var(--nxt-border-strong);
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
}
.nx-icon-rail__button:active:not(:disabled) {
  transform: scale(0.94);
}
.nx-icon-rail__button.is-active {
  border-color: var(--nxt-brand-bg);
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
  box-shadow:
    0 0 0 calc(var(--nxt-hairline) * 3) color-mix(in srgb, var(--nxt-brand-bg) 18%, transparent),
    var(--nxt-shadow-sm);
}
.nx-icon-rail__button.is-active:hover {
  background: var(--nxt-brand-bg-hover);
}
.nx-icon-rail__button:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * 2);
}
.nx-icon-rail__button:disabled {
  opacity: 0.45;
  box-shadow: none;
  cursor: not-allowed;
}

.nx-icon-rail__icon {
  width: var(--icon-rail-icon);
  height: var(--icon-rail-icon);
}

/* A brand badge on the brand-filled active button needs its own contrast. */
.nx-icon-rail__button.is-active .nx-icon-rail__badge.tone-brand {
  background: var(--nxt-surface-bg);
  color: var(--nxt-brand-subtle-fg);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-brand-bg);
}

@media (prefers-reduced-motion: reduce) {
  .nx-icon-rail__button {
    transition: none;
  }
  .nx-icon-rail__button:active:not(:disabled) {
    transform: none;
  }
}
/* The same where a subtree asks for less motion: data-nx-motion="reduced"
   on an ancestor (reset.css does the durations). */
:where([data-nx-motion='reduced']) .nx-icon-rail__button {
  transition: none;
}
:where([data-nx-motion='reduced']) .nx-icon-rail__button:active:not(:disabled) {
  transform: none;
}
</style>
